import { useState, useMemo, useCallback, useEffect } from 'react';
import type { FilterGroup } from '@practium/ui';
import { useToast } from '@practium/ui';
import { fuzzyFilter, formatDate, useDebounce } from '@practium/shared';
import { useAuth } from '@practium/features/auth';
import { CATEGORY_LABELS } from '../types/productRequest.types';
import type {
	ClientProductRequestTab,
	ProductRequestFormData,
	RequestProductStatus,
} from '../types/productRequest.types';
import { ProductRequestSchema } from '../schemas/productRequest.schema';
import { useGetRequestProducts, useFilterRequestProducts, usePostRequestProducts } from '@practium/features/api';
import { RequestProduct } from '@practium/features/products/types';

const PAGE_SIZE = 10;

const STATUS_FILTER_GROUP: FilterGroup = {
	label: 'Status',
	selectionMode: 'single',
	options: [
		{ value: 'status:PENDING', label: 'Pendentes' },
		{ value: 'status:APPROVED', label: 'Aceitos' },
		{ value: 'status:REJECTED', label: 'Rejeitados' },
	],
};

export const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Categoria',
		options: [
			{ value: 'categoria:fertilizer', label: CATEGORY_LABELS.fertilizer },
			{ value: 'categoria:lime', label: CATEGORY_LABELS.lime },
			{ value: 'categoria:conditioner', label: CATEGORY_LABELS.conditioner },
			{ value: 'categoria:organic', label: CATEGORY_LABELS.organic },
			{ value: 'categoria:special', label: CATEGORY_LABELS.special },
			{ value: 'categoria:defensive', label: CATEGORY_LABELS.defensive },
		],
	},
	{
		label: 'Período',
		selectionMode: 'single',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const CATEGORY_OPTIONS = [
	{ value: 'defensive', label: CATEGORY_LABELS.defensive },
	{ value: 'fertilizer', label: CATEGORY_LABELS.fertilizer },
	{ value: 'lime', label: CATEGORY_LABELS.lime },
	{ value: 'conditioner', label: CATEGORY_LABELS.conditioner },
	{ value: 'organic', label: CATEGORY_LABELS.organic },
	{ value: 'special', label: CATEGORY_LABELS.special },
];

const STATUS_TAB_MAP: Record<Exclude<ClientProductRequestTab, 'all'>, RequestProductStatus> = {
	pending: 'PENDING',
	approved: 'APPROVED',
	rejected: 'REJECTED',
};

const EMPTY_FORM: ProductRequestFormData = {
	name: '',
	manufacturer: '',
	category: '',
	note: '',
};

export const useProductRequests = () => {
	const toast = useToast();
	const { user } = useAuth();
	const [activeTab, setActiveTab] = useState<ClientProductRequestTab>('pending');
	const [selectedRequest, setSelectedRequest] = useState<RequestProduct | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [formData, setFormData] = useState<ProductRequestFormData>(EMPTY_FORM);
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const search = useDebounce(searchQuery.trim(), 400);
	const [sortAsc, setSortAsc] = useState(false);

	const currentDate = formatDate(new Date());
	const userName = user?.name ?? '';

	const categories = filterValues
		.filter(value => value.startsWith('categoria:'))
		.map(value => value.slice('categoria:'.length));
	const selectedPeriod = filterValues.find(value => value.startsWith('periodo:'));
	const period = selectedPeriod === 'periodo:15' ? 'last15Days'
		: selectedPeriod === 'periodo:30' ? 'last30Days'
		: selectedPeriod === 'periodo:custom' && customDateFrom && customDateTo ? 'custom'
		: undefined;
	const selectedStatus = filterValues.find(value => value.startsWith('status:'))?.slice('status:'.length);
	const statusFilter = selectedStatus === 'PENDING' || selectedStatus === 'APPROVED' || selectedStatus === 'REJECTED'
		? selectedStatus : undefined;
	const filterGroups = activeTab === 'all' ? [...FILTER_GROUPS, STATUS_FILTER_GROUP] : FILTER_GROUPS;
	const requestFilters = {
		categories: categories.length ? categories : undefined,
		status: activeTab === 'all' ? statusFilter : STATUS_TAB_MAP[activeTab],
		period,
		startDate: period === 'custom' ? customDateFrom : undefined,
		endDate: period === 'custom' ? customDateTo : undefined,
	} as const;

	const hasFilters = Boolean(requestFilters.categories || period);
	const filtersKey = JSON.stringify([activeTab, requestFilters, hasFilters ? searchQuery : search]);
	const [paginationState, setPaginationState] = useState({ filtersKey, page: 1 });
	const page = paginationState.filtersKey === filtersKey ? paginationState.page : 1;
	const setPage = (nextPage: number) => setPaginationState({ filtersKey, page: nextPage });

	const listing = useGetRequestProducts({
		page,
		limit: PAGE_SIZE,
		enabled: !hasFilters,
		search: search || undefined,
		status: requestFilters.status,
	});
	const filteredListing = useFilterRequestProducts({
		...requestFilters,
		page,
		limit: PAGE_SIZE,
		enabled: hasFilters,
	});
	const activeQuery = hasFilters ? filteredListing : listing;
	const { data: requests, pagination } = activeQuery;
	const { mutateAsync: postRequestProduct } = usePostRequestProducts();

	const processedRequests = useMemo(() => {
		let result = requests;

		// TODO: Use remote search when /request-products/filter supports it.
		result = hasFilters ? fuzzyFilter(result, searchQuery, (r) =>
			[r.name, r.manufacturer, r.category].join(' ')
		) : result;

		if (sortAsc) {
			// TODO: Use remote ordering when request-products supports it.
			result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		}

		return result;
	}, [requests, searchQuery, sortAsc, hasFilters]);

	const totalPages = Math.max(1, pagination?.totalPages ?? 1);
	const canClampPage = activeQuery.isSuccess && !activeQuery.isPlaceholderData && activeQuery.fetchStatus === 'idle';
	const isPageOutOfRange = pagination !== null && page > totalPages;
	useEffect(() => {
		setPaginationState(current => {
			if (current.filtersKey !== filtersKey) return { filtersKey, page: 1 };
			if (canClampPage && current.page > totalPages) return { filtersKey, page: totalPages };
			return current;
		});
	}, [filtersKey, canClampPage, totalPages]);
	const paginatedRequests = processedRequests;

	const setField = useCallback(
		<K extends keyof ProductRequestFormData>(field: K, value: ProductRequestFormData[K]) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
		},
		[]
	);

	const clearError = useCallback((field: string) => {
		setFormErrors((prev) => {
			if (!prev[field]) return prev;
			return { ...prev, [field]: '' };
		});
	}, []);

	const handleOpenCreate = useCallback(() => {
		setFormData(EMPTY_FORM);
		setFormErrors({});
		setIsCreateOpen(true);
	}, []);

	const handleCloseCreate = useCallback(() => {
		setFormErrors({});
		setIsCreateOpen(false);
	}, []);

	const handleSubmit = useCallback(async () => {
		const result = ProductRequestSchema.safeParse(formData);
		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setFormErrors(fieldErrors);
			return;
		}

		try {
			await postRequestProduct({
				name: result.data.name,
				manufacturer: result.data.manufacturer,
				category: result.data.category,
				note: result.data.note ?? '',
			});
			setIsCreateOpen(false);
			setFormData(EMPTY_FORM);
			setFormErrors({});
			toast.success('Solicitação enviada com sucesso!');
		} catch {
			toast.error('Erro ao enviar a solicitação.');
		}
	}, [formData, postRequestProduct, toast]);

	const handleCustomDateChange = useCallback((from: string, to: string) => {
		setCustomDateFrom(from);
		setCustomDateTo(to);
	}, []);

	return {
		requests,
		isTableLoading: activeQuery.isPending || activeQuery.isPlaceholderData || isPageOutOfRange,
		isTableFetching: activeQuery.isFetching,
		isTableError: activeQuery.isError,
		refetchTable: async () => {
			await activeQuery.refetch();
		},
		activeTab,
		setActiveTab: (tab: ClientProductRequestTab) => {
			setActiveTab(tab);
			setFilterValues(values => values.filter(value => !value.startsWith('status:')));
		},
		selectedRequest,
		setSelectedRequest,
		isCreateOpen,
		formData,
		setField,
		formErrors,
		clearError,
		filterGroups,
		filterValues,
		setFilterValues,
		customDateFrom,
		customDateTo,
		handleCustomDateChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		setSortAsc,
		page,
		setPage,
		paginatedRequests,
		totalPages,
		currentDate,
		userName,
		handleOpenCreate,
		handleCloseCreate,
		handleSubmit,
	};
};
